import * as THREE from 'three';

import { Axial, axialKey, axialToWorld } from '../map/HexCoordinates';
import { HexCellData } from '../map/HexSurface';
import { elevationToY } from '../map/HexMetrics';
import { deckY, walkY } from '../map/RoadSurface';

/**
 * The way we are going, drawn as a dashed curve over the ground.
 *
 * Three of these exist and they are the same thing said three times over: the
 * **plan being walked**, echoed back by the server; the **destination chosen**
 * by a first click and waiting for the second; and the one the **cursor** is
 * asking about while a destination stands. Only the first is a fact — the other
 * two are worked out on the client, and the route that gets walked is always
 * the one the server sends back.
 *
 * # Why a curve and not the cells
 *
 * A polyline through hex centres turns by sixty degrees at every step, so a
 * gentle diagonal reads as a staircase — the shape of the grid rather than the
 * shape of the walk. The centres are therefore treated as control points of a
 * centripetal Catmull-Rom curve and sampled several times per cell, which
 * rounds the corners off while still passing through every centre. The route is
 * still exactly the cells the server named; only the drawing is smooth.
 *
 * # Why dashed
 *
 * It is a plan, not a thing on the ground. A solid line at this width reads as
 * something painted on the terrain; a dashed one reads as a route.
 */

/**
 * What a line of route is: the plan being walked, the destination chosen and
 * waiting for a second click, or the one the cursor is asking about.
 */
export type RouteKind = 'confirmed' | 'chosen' | 'hover';

const STYLES: Record<RouteKind, { color: number; opacity: number; order: number }> = {
  // The server's plan, in the colour it has always had.
  confirmed: { color: 0xffd166, opacity: 0.85, order: 3 },
  // Picked, not yet ordered: as firm as the plan, in the colour of a question.
  chosen: { color: 0x9fd8ff, opacity: 0.85, order: 3 },
  // Still just the cursor moving, so the faintest of the three.
  hover: { color: 0x9fd8ff, opacity: 0.4, order: 2 },
};

/** Clear of the ground without floating: well under one elevation step. */
const LIFT = 0.06;

/**
 * How many points of curve a cell of route gets.
 *
 * Measured as the angle the line turns through at a drawn vertex, on a route
 * that changes direction at every step — the worst a hex grid can produce.
 * Raw centres turn 76° on average and 120° at worst; four samples bring that to
 * 20°/91°, eight to **10°/56°**, twelve to 7°/39°. Eight is where the curve
 * stops reading as a chain of segments, and even a route across the whole
 * allowed distance is then some 1500 points — nothing, for one line.
 */
const SAMPLES_PER_CELL = 8;

/** Dash and gap in world units, against a hex 0,87 across. */
const DASH = 0.14;
const GAP = 0.1;

export class RouteLine {
  readonly object: THREE.Line;

  private cells: Axial[] = [];

  constructor(
    private readonly world: Map<number, HexCellData>,
    kind: RouteKind = 'confirmed'
  ) {
    const style = STYLES[kind];
    const material = new THREE.LineDashedMaterial({
      color: style.color,
      transparent: true,
      opacity: style.opacity,
      dashSize: DASH,
      gapSize: GAP,
      // Visible through hills: it is a plan, not something standing on the map.
      depthTest: false,
    });

    this.object = new THREE.Line(new THREE.BufferGeometry(), material);
    this.object.renderOrder = style.order;
    this.object.visible = false;
    this.object.frustumCulled = false;
  }

  /** A fresh route, starting from where the player is now. */
  set(from: Axial, cells: Axial[]): void {
    this.cells = [from, ...cells];
    this.rebuild();
  }

  /** One step taken: everything up to and including that cell is behind us. */
  reached(cell: Axial): void {
    const index = this.cells.findIndex((step) => step.q === cell.q && step.r === cell.r);
    if (index < 0) return;

    this.cells = this.cells.slice(index);
    this.rebuild();
  }

  clear(): void {
    this.cells = [];
    this.rebuild();
  }

  /** Re-read heights, for when the ground under the route streams in. */
  refresh(): void {
    if (this.cells.length > 1) this.rebuild();
  }

  /** Where the line currently ends, for a caller deciding whether to redraw. */
  get endsAt(): Axial | undefined {
    return this.cells[this.cells.length - 1];
  }

  private rebuild(): void {
    if (this.cells.length < 2) {
      this.object.visible = false;
      return;
    }

    const centres = this.cells.map((cell) => {
      const { x, z } = axialToWorld(cell.q, cell.r);
      const data = this.world.get(axialKey(cell.q, cell.r));
      const ground = data ? (deckY(data) ?? elevationToY(data.elevation)) : 0;
      return new THREE.Vector3(x, ground + LIFT, z);
    });

    // Centripetal rather than uniform: on a route that doubles back around an
    // obstacle — which is most of what a route is for — the uniform version
    // overshoots into a loop outside the cells it is meant to run through.
    const curve = new THREE.CatmullRomCurve3(centres, false, 'centripetal');
    const points = curve.getPoints((centres.length - 1) * SAMPLES_PER_CELL);
    // **Po tym, po czym się chodzi**: każdy punkt siada na pokładzie mostu albo
    // na terenie pod sobą. Krzywa przez same środki pól szła po dnie pod mostem
    // i cięła stoki na skos.
    for (const point of points) {
      const ground = walkY(this.world, point.x, point.z);
      if (ground !== null) point.y = ground + LIFT;
    }

    this.object.geometry.dispose();
    this.object.geometry = new THREE.BufferGeometry().setFromPoints(points);
    // Dashes are measured along the line, and the line has to be asked for its
    // own lengths before the material can space them.
    this.object.computeLineDistances();
    this.object.visible = true;
  }

  dispose(): void {
    this.object.geometry.dispose();
    (this.object.material as THREE.Material).dispose();
  }
}
